// External Imports
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MdAdd, MdRefresh } from "react-icons/md";

// Internal Imports
import { UtilityContext } from "../../context/util-context";
import Heading from "../UI/Heading";
import SearchBar from "../UI/SearchBar";
import FilterMenu from "../UI/FilterMenu";
import DateFilter from "../UI/DateFilter";
import { requestAdminAPI } from "../../api/posh-api";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import AddCoupon from "./AddCoupon";
import CouponInfo from "./CouponInfo";
import ExportCsvButton from "../UI/ExportCsvButton";
import PaginationMenu from "../UI/PaginationMenu";
import { formatCurrency } from "../../util/format-text";

/** ========================================================================
 * * Coupons Page
 * Renders a page, which displays all coupons.
 * ========================================================================== */

export default function CouponsPage() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filters
  const defaultFilters = {
    query: "",
    limit: 50,
    reusable: "All",
    exclusive: "All",
    used: "All",
    sd: "",
    ed: "",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  // Initialize States
  const [coupons, setCoupons] = useState([]);
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(true);
  const [add, setAdd] = useState(false);
  const [selected, setSelected] = useState();

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      // Send Request
      const data = await requestAdminAPI("search", {
        table: "coupon",
        limit: filter.limit,
        filters: filter,
      });

      // Replace URL
      let url = "/coupons?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setCoupons(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== FILTER OPTIONS ===================
  const filterOptions = [
    {
      label: "Reusable",
      options: ["All", "Non Reusable", "Reusable"],
      selected: filter.reusable,
    },
    {
      label: "Exclusive",
      options: ["All", "Yes", "No"],
      selected: filter.exclusive,
    },
    {
      label: "Used",
      options: ["All", "Used", "Not Used"],
      selected: filter.used,
    },
  ];

  // Fetch data on load and when filter changes
  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="container grid">
      <AddCoupon open={add} setOpen={setAdd} onComplete={fetchRecords} />
      <CouponInfo
        open={selected ? true : false}
        setOpen={setSelected}
        onComplete={fetchRecords}
        couponId={selected}
      />
      <Heading title="Coupons" isBackArrow={true}>
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <FilterMenu
          onSubmit={(f) => setFilter({ ...filter, ...f })}
          options={filterOptions}
        />
        <MdAdd onClick={() => setAdd(true)} className="text-3xl icon" />
        <MdRefresh onClick={fetchRecords} className="text-2xl icon" />
        <ExportCsvButton array={coupons} filename="coupons" />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      {loading ? (
        <LoadingCard columns={5} max={6} />
      ) : coupons.length > 0 ? (
        <div className="list">
          {coupons.map((c, idx) => (
            <CouponCard
              key={idx}
              coupon={c}
              onClick={() => setSelected(c.id)}
            />
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        limit={filter.limit}
        setLimit={(limit) => setFilter({ ...filter, limit })}
        loading={loading}
        count={coupons.length}
      />
    </div>
  );
}

const CouponCard = ({ coupon, onClick }) => {
  const classNames = () => {
    let classes = "grid grid-cols-5 text-sm pill text-stone-400 ";
    if (coupon.used && coupon.reusable) classes += "border-green-600";
    else if (!coupon.used) classes += "border-yellow-600";
    return classes;
  };
  return (
    <div onClick={onClick} className={classNames()}>
      <p className="mx-auto text-stone-200">{coupon.id}</p>
      <p>
        {coupon.type === "percentage"
          ? `${Number(coupon.amount) * 100}%`
          : `${formatCurrency(coupon.amount)}`}
      </p>
      <p>{coupon.reusable ? "Reusable" : "Not Reusable"}</p>
      <p>{coupon.customerId ? "Exclusive" : "Not Exclusive"}</p>
      <p>{coupon.used ? "Used" : "Not Used"}</p>
    </div>
  );
};
